import { useState, useCallback } from "react";

export const useFormState = (
  config,
  entityHandler,
  userDetails,
  allItems = []
) => {
  const [formData, setFormData] = useState({});
  const [originalFormData, setOriginalFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  // Initialize form data from selected item
  const initializeFormData = useCallback(
    (item) => {
      const initialData = {};
      config.fields.forEach((field) => {
        initialData[field.key] = entityHandler.getFieldValue(
          field,
          item,
          userDetails
        );
      });
      setFormData(initialData);
      setOriginalFormData(JSON.parse(JSON.stringify(initialData)));
      setValidationErrors({});
    },
    [config.fields, entityHandler, userDetails]
  );

  const isFormDataChanged = useCallback(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  }, [formData, originalFormData]);

  const validateForm = useCallback(
    (currentSelectedItem = null) => {
      const errors = {};

      config.fields.forEach((field) => {
        const value = formData[field.key];

        // Check for required fields (marked with *)
        if (field.label.includes("*")) {
          if (!value || value.toString().trim() === "") {
            errors[field.key] = `${field.label.replace("*", "")} is required`;
          }
        }

        // Check for duplicate names
        if (field.key === "name" && value && value.toString().trim() !== "") {
          const trimmedValue = value.toString().trim();
          const isDuplicate = allItems.some((item) => {
            // Skip the current item being edited
            if (currentSelectedItem && item.id === currentSelectedItem.id) {
              return false;
            }
            return item.name === trimmedValue;
          });

          if (isDuplicate) {
            const itemType = config.itemLabel.toLowerCase();
            errors[field.key] = `A ${itemType} with this name already exists`;
          }
        }

        // Email validation for email fields
        if (field.key === "email" && value && value.toString().trim() !== "") {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            errors[field.key] = "Please enter a valid email address";
          }
        }
      });

      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    },
    [config.fields, config.itemLabel, formData, allItems]
  );

  // Reset form to original state
  const resetForm = useCallback(() => {
    setFormData(JSON.parse(JSON.stringify(originalFormData)));
    setValidationErrors({});
  }, [originalFormData]);

  // Mark form as saved (update original data)
  const markFormSaved = useCallback(() => {
    setOriginalFormData(JSON.parse(JSON.stringify(formData)));
  }, [formData]);

  return {
    formData,
    setFormData,
    originalFormData,
    validationErrors,
    initializeFormData,
    isFormDataChanged,
    validateForm,
    resetForm,
    markFormSaved,
  };
};
